import type { MarginPlanInputs } from "./marginProjection";
import { normalizeMarginPlanInputs } from "./marginPlanStorage";
import { sortableExportFilename } from "./exportFilename";

const CONFIG_FORMAT = "smart-margin/planner-configuration";
const CONFIG_VERSION = 1;

export interface MarginPlanConfigTransfer {
  format: typeof CONFIG_FORMAT;
  version: typeof CONFIG_VERSION;
  exportedAt: string;
  configuration: {
    name: string;
    inputs: MarginPlanInputs;
  };
}

export function buildMarginPlanConfigTransfer(
  name: string,
  inputs: MarginPlanInputs,
  exportedAt = new Date().toISOString(),
): MarginPlanConfigTransfer {
  const normalizedName = name.trim();
  if (!normalizedName) throw new Error("Save and select a named configuration first.");
  return {
    format: CONFIG_FORMAT,
    version: CONFIG_VERSION,
    exportedAt,
    configuration: {
      name: normalizedName,
      inputs: structuredClone(inputs),
    },
  };
}

export function parseMarginPlanConfigTransfer(
  source: string,
  fallback: MarginPlanInputs,
): { name: string; inputs: MarginPlanInputs } {
  let parsed: unknown;
  try {
    parsed = JSON.parse(source);
  } catch {
    throw new Error("The selected file is not valid JSON.");
  }
  if (!parsed || typeof parsed !== "object") {
    throw new Error("The selected file is not a planner configuration.");
  }
  const candidate = parsed as Record<string, unknown>;
  if (candidate.format !== CONFIG_FORMAT || candidate.version !== CONFIG_VERSION) {
    throw new Error("This planner configuration format or version is not supported.");
  }
  const configuration = candidate.configuration;
  if (!configuration || typeof configuration !== "object") {
    throw new Error("The planner configuration is missing its settings.");
  }
  const record = configuration as Record<string, unknown>;
  const name = typeof record.name === "string" ? record.name.trim() : "";
  if (!name || name.length > 60) {
    throw new Error("The planner configuration name is missing or too long.");
  }
  const inputs = normalizeMarginPlanInputs(record.inputs, fallback);
  if (!inputs) throw new Error("The planner configuration settings are invalid.");
  return { name, inputs };
}

export function downloadMarginPlanConfig(configuration: MarginPlanConfigTransfer): void {
  const blob = new Blob([JSON.stringify(configuration, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = sortableExportFilename(
    configuration.configuration.name,
    "planner-config",
    "smart-margin.json",
    configuration.exportedAt,
  );
  link.click();
  URL.revokeObjectURL(url);
}
